import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import { useStyleTemplate } from '../contexts/StyleTemplateContext';
import { DEFAULT_HEATMAP } from '../theme';
import { valueToHeatColor } from '../utils/heatmapColor';
import { isDataImageUrl, isRemoteImageUrl, localizeImageUrl } from '../utils/localizeImageUrl';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

function resolveImageSrc(url) {
  const s = String(url || '').trim();
  if (!s) return '';
  if (isDataImageUrl(s) || isRemoteImageUrl(s)) return s;
  if (s.startsWith('/') && typeof window !== 'undefined') {
    return `${window.location.origin}${s}`;
  }
  return s;
}

function resolveRowColumn(row, columnName) {
  if (!row || columnName == null || columnName === '') return undefined;
  if (Object.prototype.hasOwnProperty.call(row, columnName)) return row[columnName];
  const lower = String(columnName).toLowerCase();
  const hit = Object.keys(row).find((k) => String(k).toLowerCase() === lower);
  return hit ? row[hit] : undefined;
}

const HeatmapGridWidget = ({
  data,
  heatmapGridConfig,
  editMode = false,
  onCellAssignmentChange,
  availableColumns = [],
  getAuthHeaders,
}) => {
  const localizeImageMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/chart-builder/localize-image', body, { headers }),
  });

  const { heatmap: heatmapPalette } = useStyleTemplate();
  const heatmapConfig = heatmapPalette ? { ...DEFAULT_HEATMAP, ...heatmapPalette } : DEFAULT_HEATMAP;
  const cellOpacity = heatmapConfig.cellOpacity != null ? heatmapConfig.cellOpacity : DEFAULT_HEATMAP.cellOpacity;

  const [imgError, setImgError] = useState(false);
  const [resolvedBgUrl, setResolvedBgUrl] = useState('');
  const [bgReady, setBgReady] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editingCell, setEditingCell] = useState(null); // { r, c }
  const [draftFilterValue, setDraftFilterValue] = useState('');

  const config = heatmapGridConfig || {};
  const {
    backgroundImageUrl,
    gridRows: configRows,
    gridCols: configCols,
    showValues = true,
    showFilterValueName = false,
    valueUnit = '',
    valueColumn: configValueColumn,
    filterColumn: configFilterColumn,
    secondaryValueColumn: configSecondaryValueColumn = '',
    cellAssignments = {},
  } = config;

  const numRows = Math.max(1, (configRows != null && configRows > 0) ? Number(configRows) : 2);
  const numCols = Math.max(1, (configCols != null && configCols > 0) ? Number(configCols) : 2);

  // Valores únicos de la columna del filtro (para el menú de cada celda)
  const uniqueFilterValues = useMemo(() => {
    if (!data?.length || !configFilterColumn) return [];
    const vals = new Set();
    data.forEach((row) => {
      const v = resolveRowColumn(row, configFilterColumn);
      if (v !== undefined && v !== null && v !== '') vals.add(String(v));
    });
    return [...vals].sort();
  }, [data, configFilterColumn]);

  // En modo vista: valor por celda = sum(valueColumn) donde filterColumn === asignación.filterValue; valor secundario = sum(secondaryValueColumn) igual
  const { cellValues, cellSecondaryValues, minVal, maxVal } = useMemo(() => {
    const values = {};
    const secondaryValues = {};
    let minV = Infinity;
    let maxV = -Infinity;
    if (!data?.length || !configValueColumn || !configFilterColumn) {
      return { cellValues: values, cellSecondaryValues: secondaryValues, minVal: 0, maxVal: 1 };
    }

    Object.keys(cellAssignments).forEach((key) => {
      const a = cellAssignments[key];
      const filterVal = a?.filterValue != null ? String(a.filterValue) : null;
      if (filterVal === null || filterVal === '') return;
      const filtered = data.filter((row) => String(resolveRowColumn(row, configFilterColumn)) === filterVal);
      const value = filtered.reduce(
        (sum, row) => sum + (parseFloat(resolveRowColumn(row, configValueColumn)) || 0),
        0
      );
      values[key] = value;
      if (value < minV) minV = value;
      if (value > maxV) maxV = value;
      if (configSecondaryValueColumn) {
        const rawVals = filtered.map((row) => resolveRowColumn(row, configSecondaryValueColumn));
        const isStrictNumber = (v) => { const s = String(v).trim(); return s !== '' && !Number.isNaN(parseFloat(s)) && /^-?\d*\.?\d+$/.test(s); };
        const allNumeric = rawVals.every((v) => v !== undefined && v !== null && v !== '' && isStrictNumber(v));
        if (allNumeric && rawVals.length > 0) {
          const sec = filtered.reduce(
            (sum, row) => sum + (parseFloat(resolveRowColumn(row, configSecondaryValueColumn)) || 0),
            0
          );
          secondaryValues[key] = sec;
        } else if (rawVals.length > 0) {
          // Mostrar el valor como texto (ej: "00 to 01 hrs"); si todos iguales uno, si no el primero
          const first = rawVals[0];
          secondaryValues[key] = first !== undefined && first !== null && first !== '' ? String(first) : null;
        }
      }
    });
    return {
      cellValues: values,
      cellSecondaryValues: secondaryValues,
      minVal: minV === Infinity ? 0 : minV,
      maxVal: maxV === -Infinity ? 1 : maxV,
    };
  }, [data, cellAssignments, configValueColumn, configFilterColumn, configSecondaryValueColumn]);

  const span = maxVal - minVal || 1;

  useEffect(() => {
    let cancelled = false;
    setImgError(false);
    setBgReady(false);

    const raw = String(backgroundImageUrl || '').trim();
    if (!raw) {
      setResolvedBgUrl('');
      setBgReady(true);
      return undefined;
    }

    if (isDataImageUrl(raw)) {
      setResolvedBgUrl(raw);
      return undefined;
    }

    const src = resolveImageSrc(raw);
    if (isRemoteImageUrl(src)) {
      localizeImageUrl(src, getAuthHeaders, (body, config) =>
        localizeImageMutation.mutateAsync({ body, headers: config.headers })
      )
        .then((dataUrl) => {
          if (!cancelled) setResolvedBgUrl(dataUrl || src);
        })
        .catch(() => {
          if (!cancelled) setResolvedBgUrl(src);
        });
      return () => {
        cancelled = true;
      };
    }

    setResolvedBgUrl(src);
    return undefined;
  }, [backgroundImageUrl, getAuthHeaders]);

  const displayBgUrl = resolvedBgUrl || resolveImageSrc(backgroundImageUrl);
  const needsBackground = Boolean(displayBgUrl);

  const handleCellClick = (e, r, c) => {
    if (!editMode || !onCellAssignmentChange) return;
    const key = `${r},${c}`;
    const a = cellAssignments[key] || {};
    setEditingCell({ r, c });
    setDraftFilterValue(a.filterValue != null ? String(a.filterValue) : '');
    setAnchorEl(e.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setEditingCell(null);
  };

  const handleSaveAssignment = () => {
    if (!editingCell || !onCellAssignmentChange) return;
    onCellAssignmentChange(editingCell.r, editingCell.c, {
      filterValue: draftFilterValue !== '' ? draftFilterValue : undefined,
    });
    handleClosePopover();
  };

  const handleClearAssignment = () => {
    if (!editingCell || !onCellAssignmentChange) return;
    onCellAssignmentChange(editingCell.r, editingCell.c, null);
    handleClosePopover();
  };

  return (
    <Box
      className="chart-heatmap-host"
      data-chart-needs-bg={needsBackground ? 'true' : undefined}
      data-chart-visual-ready={!needsBackground || bgReady ? 'true' : undefined}
      sx={{
        height: '100%',
        minHeight: 200,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 1,
        isolation: 'isolate',
      }}
    >
      {displayBgUrl && !imgError && (
        <Box
          component="img"
          src={displayBgUrl}
          crossOrigin={isRemoteImageUrl(displayBgUrl) ? 'anonymous' : undefined}
          onLoad={() => setBgReady(true)}
          onError={() => {
            setImgError(true);
            setBgReady(true);
          }}
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.4,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
      )}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          display: 'grid',
          gridTemplateRows: `repeat(${numRows}, 1fr)`,
          gridTemplateColumns: `repeat(${numCols}, 1fr)`,
          gap: 1,
          p: 1,
          boxSizing: 'border-box',
        }}
      >
        {Array.from({ length: numRows * numCols }, (_, i) => {
          const r = Math.floor(i / numCols);
          const c = i % numCols;
          const key = `${r},${c}`;
          const assignment = cellAssignments[key];
          let val = null;
          let secVal = null;
          let isNum = false;
          let t = NaN;
          if (assignment?.filterValue != null && configValueColumn && configFilterColumn) {
            val = cellValues[key];
            secVal = cellSecondaryValues[key];
            isNum = typeof val === 'number' && !Number.isNaN(val);
            t = isNum ? (val - minVal) / span : NaN;
          }
          const emptyCellBg = `rgba(0,0,0,${Math.max(0.02, 1 - Number(cellOpacity))})`;
          const assignedEmptyBg = `rgba(255,255,255,${Number(cellOpacity)})`;
          const bg = isNum ? valueToHeatColor(t, heatmapConfig) : (assignment ? assignedEmptyBg : emptyCellBg);
          return (
            <Box
              key={key}
              onClick={(e) => handleCellClick(e, r, c)}
              sx={{
                backgroundColor: bg,
                borderRadius: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 28,
                border: editMode ? '2px solid rgba(0,0,0,0.2)' : '1px solid rgba(0,0,0,0.06)',
                cursor: editMode ? 'pointer' : 'default',
                '&:hover': editMode ? { borderColor: 'primary.main', boxShadow: 'inset 0 0 0 1px rgba(25,118,210,0.35)' } : {},
              }}
            >
              {editMode ? (
                assignment?.filterValue != null ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {String(assignment.filterValue)}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">+</Typography>
                )
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                  {showValues && isNum && (
                    <Typography variant="caption" sx={{ fontWeight: 600, color: t > 0.5 ? '#fff' : '#333', textShadow: '0 0 1px rgba(0,0,0,0.3)' }}>
                      {Number(val).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      {valueUnit ? ` ${valueUnit.trim()}` : ''}
                    </Typography>
                  )}
                  {configSecondaryValueColumn && (secVal !== undefined && secVal !== null && secVal !== '') && (
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: t > 0.5 ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.6)', textShadow: '0 0 1px rgba(0,0,0,0.2)' }}>
                      {typeof secVal === 'number' ? Number(secVal).toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(secVal)}
                    </Typography>
                  )}
                  {showFilterValueName && assignment?.filterValue != null && (
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: t > 0.5 ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.7)', textShadow: '0 0 1px rgba(0,0,0,0.2)' }}>
                      {String(assignment.filterValue)}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { p: 2, minWidth: 200, maxWidth: 280 } }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Celda ({editingCell?.r}, {editingCell?.c})
        </Typography>
        {configFilterColumn ? (
          <>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Elige el valor por el que filtrar (columna &quot;{configFilterColumn}&quot;). El valor mostrado viene de la columna de dato definida en propiedades.
            </Typography>
            <FormControl size="small" sx={{ mt: 1, width: '100%', maxWidth: 220 }}
              fullWidth={false}
            >
              <InputLabel id="heatmap-cell-filter-label" shrink>Valor del filtro</InputLabel>
              <Select
                labelId="heatmap-cell-filter-label"
                value={draftFilterValue}
                label="Valor del filtro"
                onChange={(e) => setDraftFilterValue(e.target.value)}
                displayEmpty
                renderValue={(v) => (v === '' ? 'Ninguno' : v)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">Ninguno</MenuItem>
                {uniqueFilterValues.map((v) => (
                  <MenuItem key={v} value={v}>{v}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Define la columna del filtro en el panel de propiedades (izquierda) para poder elegir el valor en cada celda.
          </Typography>
        )}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={handleClearAssignment}>Quitar</Button>
          <Button size="small" variant="contained" onClick={handleSaveAssignment} disabled={!configFilterColumn || !draftFilterValue}>
            Aplicar
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default HeatmapGridWidget;
